/**
 * Lib.Common: v-privacy directive
 *
 * @file
 * If `$app.option.privacyMode` is `true`, display a privacy icon with optional dots in place of the element's content.
 *
 * @modifier noclick - Do not toggle privacy mode on click (this is a feature of iTWS and web platforms only).
 * @modifier noicon - Do not include icon in placeholder.
 *
 * @param {number} - The number of dots to display in the privacy placeholder.
 * @value {boolean} - If `false`, do not actually apply privacy masking to this element.
 *
 * @example
 * <template lang="pug">
 * div(v-privacy) Click me
 * </template>
 */


import $app from '../global/app';
import icon from '../icon/misc/watchlist.svg';


const haveClickToggle =
    $build.platform == 'ios' ||
    $build.platform == 'web' ||
    $build.platform == 'marketing';


const inst = new Set();
const pph = (places,noicon) => {
    const span = document.createElement('span');
    span.className = '_pph';
    span.innerHTML = '•'.repeat(places) + (noicon ? '' : '\xa0' + icon);
    return span;
};

let privacyMode = false;
let placeholder = [
    pph(0,1),
    pph(1,1),
    pph(2,1),
    pph(3,1),
    pph(4,1),
    pph(5,1),
    pph(6,1),
    pph(7,1),
    pph(8,1),
    pph(9,1)
];
let placeholderIcon = [
    pph(0),
    pph(1),
    pph(2),
    pph(3),
    pph(4),
    pph(5),
    pph(6),
    pph(7),
    pph(8),
    pph(9)
];


function click(e) {
    $app.option.privacyMode = !$app.option.privacyMode;
    e.stopPropagation();
}

function update(el, binding) {
    const privacy = el.__privacy__;
    if (binding.value !== false) {
        if (privacy.value === false) {
            privacy.value = binding.value;
            if (haveClickToggle && !binding.modifiers.noclick) {
                el.classList.add('_ppp');
                el.addEventListener('click', click);
            }
            inst.add(el);
        }
        if (privacyMode) {
            const arg = binding.arg >= 0 && binding.arg <= 9 ? binding.arg : 4;
            if (el.firstChild !== privacy.node) {
                privacy.arg = arg;
                privacy.cache = [...el.childNodes];
                privacy.node = (binding.modifiers.noicon ? placeholder : placeholderIcon)[arg].cloneNode(true);
                el.replaceChildren(privacy.node);
            } else if (privacy.arg !== arg) {
                privacy.node = (binding.modifiers.noicon ? placeholder : placeholderIcon)[arg].cloneNode(true);
                el.replaceChildren(privacy.node);
            }
        }
    } else {
        if (privacy.value !== false) {
            privacy.value = binding.value;
            if (haveClickToggle && !binding.modifiers.noclick) {
                el.classList.remove('_ppp');
                el.removeEventListener('click', click);
            }
            inst.delete(el);
        }
    }
    if (!privacyMode && privacy.cache !== void 0) {
        if (privacy.html !== void 0) {
            el.innerHTML = privacy.html;
            privacy.html = void 0;
        } else {
            el.replaceChildren(...privacy.cache);
        }
        privacy.cache = void 0;
        privacy.node = void 0;
    }
}

function bind(el, binding) {
    el.__privacy__ = {
        value: false,
        modifiers: binding.modifiers,
        cache: void 0,
        node: void 0
    };
    update(el, binding);
}

function unbind(el) {
    update(el, {
        value: false,
        modifiers: el.__privacy__.modifiers
    });
}

function setPrivacyMode(mode) {
    privacyMode = mode;
    inst.forEach(el => {
        update(el, el.__privacy__);
    });
}


export {
    setPrivacyMode
};
export default {
    name: 'privacy',
    directive: {
        bind,
        unbind,
        update
    }
};
